import { Customers } from '../collections/customers';
import { Organisations } from '../collections/organisations';
import { AvailabilityStatusEnum } from '../models/availabilityStatusEnum';
import { Customer } from '../models/customer';
import { Job } from '../models/job';
import { PoolerUser, User } from '../models/user';

// Used to do serverside checks if invitee can be booked on a job
// to add a layer of security
// don't check availability because poolers can apply for possibleJobs when unavailable
export function canBookInviteeForJob(
  inviteeUser: PoolerUser,
  job: Job,
  customerOfInvitee?: Customer,
  disregardAvailability?: boolean,
  /** Confirmed in transfer flow when the dispo still wants to rebook to an unassigned station. */
  disregardDepartmentNotAssigned?: boolean,
): { canBook: boolean; reason?: string } {
  // Guard against undefined user - if user is undefined, they can be booked
  // (this allows the booking process to continue when user data is not yet loaded)
  if (!inviteeUser) {
    return {
      canBook: true,
    };
  }

  if (userHasBlacklistedDepartment(inviteeUser, job, customerOfInvitee)) {
    return {
      canBook: false,
      reason: 'cantBookReasonUserBlacklistedDepartment',
    };
  }

  if (
    !disregardDepartmentNotAssigned &&
    !userIsExternal(inviteeUser, job) &&
    !userHasJobDepartmentAssigned(inviteeUser, job)
  ) {
    return {
      canBook: false,
      reason: 'cantBookReasonDepartmentNotAssigned',
    };
  }

  if (!isAvailableForJob(inviteeUser, job) && !disregardAvailability) {
    return {
      canBook: false,
      reason: 'cantBookReasonNotAvailable',
    };
  }

  if (departmentHasUserBlacklisted(inviteeUser, job)) {
    return {
      canBook: false,
      reason: 'cantBookReasonDepartmentBlacklistedUser',
    };
  }

  return {
    canBook: true,
  };
}

function userHasJobDepartmentAssigned(inviteeUser: PoolerUser, job: Job) {
  // does not respect current customer for reduced complexity
  let hasDepartment = false;
  if (
    inviteeUser.customerInformation &&
    Object.keys(inviteeUser.customerInformation).length
  ) {
    Object.keys(inviteeUser.customerInformation).forEach((key) => {
      if (
        inviteeUser.customerInformation?.[key].departmentIds?.includes(
          job.department.id,
        )
      ) {
        hasDepartment = true;
      }
    });
  }
  return hasDepartment;
}

function departmentHasUserBlacklisted(inviteeUser: PoolerUser, job: Job) {
  const organisation = Organisations.findOne({
    'departments.id': job.department.id,
  });
  const department = organisation?.departments.find(
    (department) => department.id === job.department.id,
  );

  return department?.blacklistedUsers?.find(
    (blacklistedUser) => blacklistedUser.id === inviteeUser._id,
  );
}

function userHasBlacklistedDepartment(
  inviteeUser: PoolerUser,
  job: Job,
  customerOfInvitee?: Customer,
) {
  return (
    inviteeUser.userPreferenceDepartmentBlacklist &&
    inviteeUser.userPreferenceDepartmentBlacklist.includes(job.department.id) &&
    !customerOfInvitee?.allowIgnoreUserPreferenceDepartmentBlacklist
  );
}
function userIsExternal(inviteeUser: User, job: Job) {
  return job.invitees.some(
    (invite) => invite.userId == inviteeUser._id && invite.customer.isExternal,
  );
}

function isAvailableForJob(user: PoolerUser, job: Job) {
  return !user.availabilities.some((availability) => {
    return (
      availability.date === job.date &&
      job.shifts.some((shift) => {
        return (
          availability.shifts?.[shift.shift] &&
          availability.shifts?.[shift.shift] === AvailabilityStatusEnum.WORKING
        );
      })
    );
  });
}

export function isAllowedOnJob(user: PoolerUser, job: Job) {
  let customersOfUser = Customers.find({
    'pool.activeUserIds': user._id,
  }).fetch();
  return customersOfUser?.some((customer) => {
    return (
      job.customer.id === customer._id ||
      job.transfers?.some((transfer) => {
        return (
          transfer.targetCustomer.id === customer._id && transfer.transferredAt
        );
      }) ||
      customer.pool.jobsSharedWithCustomerIds?.includes(job.customer.id)
    );
  });
}

export function userIncludesRequiredFieldsForJob(
  user: PoolerUser,
  customer: Customer,
) {
  if (customer.requiredJobFields?.dateOfBirth && !user.dateOfBirth) {
    return false;
  } else if (
    customer.requiredJobFields?.certificates &&
    !user.certificates?.length
  ) {
    return false;
  }
  return true;
}

export function userIncludesRequiredFieldsForEmployment(
  user: PoolerUser,
  customer: Customer,
) {
  if (customer.requiredEmploymentFields?.dateOfBirth && !user.dateOfBirth) {
    return false;
  } else if (
    customer.requiredEmploymentFields?.certificates &&
    !user.certificates?.length
  ) {
    return false;
  }
  return true;
}
